import React, { useEffect, useCallback, ReactElement, useContext } from 'react';
import ProgressIndicator from '@components/ProgressIndicator';
import ShoppingCart from '@providers/ShoppingCart';
import { gaEvent } from '@helpers/analytics';
import { getEcommerceItemFromProduct } from '@helpers/ecommerce';
import ReactGA from 'react-ga';
import { Interval } from '@typings/graphql-models';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import { useRouter } from 'next/router';
import { getId } from '@helpers/helper';
import { useUser } from '@hooks';
import { getProductBySlug, isProductBuy } from '@tsClient';
import { useIsUserInitialized } from '@providers/Auth';
import TeamsContext from '@providers/Teams/TeamsContext';

type Props = {
  interval: string | null;
  priceId: string | null;
  data: Awaited<ReturnType<typeof getProductBySlug>>;
  isFreeMembership: boolean;
};

type Price = Props['data']['prices'][0];

const Purchase = ({ data, interval, priceId, isFreeMembership }: Props): ReactElement => {
  const product = data;
  const router = useRouter();
  const [user] = useUser();
  const { creatorId } = useContext(TeamsContext);
  const isUserInitialized = useIsUserInitialized();
  const { creator } = product;
  const getActivePrice = (): Price | null => {
    if (product?.prices?.length) {
      if (priceId) {
        const price = product.prices.find(p => p.id === priceId);
        if (price) {
          return price;
        }
      }
      const yearPrice = product.prices.find(({ recurringInterval }) => Interval.Year === recurringInterval);
      const monthPrice = product.prices.find(({ recurringInterval }) => Interval.Month === recurringInterval);
      if (interval) {
        const price = interval.toUpperCase() === Interval.Year ? yearPrice : monthPrice;
        if (price) {
          return price;
        }
      }
      return monthPrice || product.prices[0];
    }
    return null;
  };
  const activePrice = getActivePrice();

  const buyProduct = useCallback(async () => {
    if (activePrice) {
      await ShoppingCart.addProduct({ price: activePrice });
      await router.replace(getUrl(IDENTIFIERS.CART, {
        creatorSlug: creator.slug,
        isFreeMembership,
        ...router.query,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePrice, creator.slug, isFreeMembership]);

  const redirectToHandbook = useCallback(
    async res => {
      if (res.status && product.handbookSteps.length) {
        return router.replace(
          getUrl(IDENTIFIERS.PRODUCT, {
            creatorSlug: creator.slug,
            productSlug: product.slug,
          }),
        );
      }
      if (res.status) {
        return router.replace(getUrl(IDENTIFIERS.USER_PROFILE, { creatorSlug: creator.slug }));
      }
      return buyProduct();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buyProduct, creator, product],
  );

  useEffect(() => {
    if (!isUserInitialized) {
      return;
    }
    const submitGAEvents = (): void => {
      gaEvent('begin_checkout', {
        items: [getEcommerceItemFromProduct(product)],
      });
      ReactGA.plugin.execute('ec', 'addProduct', getEcommerceItemFromProduct(product));
      ReactGA.plugin.execute('ec', 'setAction', 'add');
      ReactGA.plugin.execute('ec', 'setAction', 'checkout');
    };
    submitGAEvents();
    if (user?.id && !user.isAnonymous && creatorId !== creator.id) {
      isProductBuy({ id: getId(product.id) })
        .then(res => redirectToHandbook(res))
        .catch(e => {
          // eslint-disable-next-line
          console.log(e);
          return null;
        });
    } else {
      buyProduct();
    }
  }, [buyProduct, product, redirectToHandbook, user, creator, isUserInitialized]);
  return (
    <div
      style={{
        display: 'flex',
        height: 24,
        justifyContent: 'center',
        padding: 24,
        width: '100%',
      }}
    >
      <ProgressIndicator />
    </div>
  );
};

export default Purchase;
